import { useMutation } from "@tanstack/react-query";
import {
  createState,
  pluralize,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  notifyError,
  notifySuccess,
  TrashcanIcon,
} from "@theprelude/ds";
import { useOverlay } from "@/hooks/use-overlay";
import { DefaultDialog } from "../default-dialog";

export const useDeleteEndpointModal = createState<boolean>(false);
export function DeleteEndpointModal({
  endpointIds,
}: {
  endpointIds: string[];
}) {
  const deleteEndpointModal = useDeleteEndpointModal((state) => state.value);
  const setDeleteEndpointModal = useDeleteEndpointModal(
    (state) => state.setValue,
  );
  const authConfig = useAuthConfig();
  const overlay = useOverlay();

  const deleteEndpoint = useMutation({
    mutationFn: async () => {
      return Promise.all(
        endpointIds.map((endpoint_id) =>
          service(authConfig).detect.deleteEndpoint(endpoint_id),
        ),
      );
    },
    onSuccess: async () => {
      notifySuccess({
        message: `Successfully deleted ${endpointIds.length} ${pluralize(
          endpointIds.length,
          "endpoints",
        )}`,
        dismissible: true,
      });
      setDeleteEndpointModal(false);
      void overlay.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to delete endpoints",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <DefaultDialog
      open={deleteEndpointModal}
      onOpenChange={setDeleteEndpointModal}
    >
      <DialogHeader>
        <DialogTitle>
          Are you sure you want to delete the following endpoints?
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div>This will remove the following endpoints:</div>
        <ul className="max-h-40 list-inside list-disc space-y-1 overflow-auto p-4">
          {endpointIds.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button
          intent="negative"
          loading={deleteEndpoint.isPending}
          disabled={deleteEndpoint.isPending}
          icon={<TrashcanIcon />}
          onClick={() => deleteEndpoint.mutate()}
        >
          Delete endpoints
        </Button>
      </DialogFooter>
    </DefaultDialog>
  );
}
